import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Send,
  Check,
  Info,
  ContactRound,
  Users,
  RotateCcw,
  ChevronDown,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUSES, STATUS_META, OUR_SIDE, DEFAULT_COMMITTEE } from "@/lib/constants";
import { birincilCep, telefonlar, numaraYaz, waLinki, basHarfBuyuk, vcardMetni } from "@/lib/telefon";

// Gönderim işaretleri ve kaydedilen mesaj metni yalnız bu cihazda tutulur:
// davet gönderen kişinin kendi takibi, veritabanına yazılmaz.
const GONDERILEN_ANAHTAR = "davet_gonderilen_v1";
const METIN_ANAHTAR = "davet_metin_v2";
const KISA_AD = DEFAULT_COMMITTEE.includes("OTURMA") ? "MOG" : "MTP";

const VARSAYILAN_METIN =
  "Sayın {ad},\n\n" +
  "Umarız sağlıklı ve huzurlu bir gün geçiriyorsunuzdur.\n\n" +
  "3 Ekim Cumartesi günü İnegöl Ticaret ve Sanayi Odası seçimleri yapılacaktır. " +
  "Sandığa gelerek oyunuzu kullanmanız ve bu önemli günde bizlerle birlikte olmanız " +
  "bizim için çok değerlidir.\n\n" +
  "Katılımınız için şimdiden teşekkür eder, saygılarımızı sunarız.";

const oku = (anahtar, varsayilan) => {
  try {
    const v = localStorage.getItem(anahtar);
    return v == null ? varsayilan : JSON.parse(v);
  } catch {
    return varsayilan;
  }
};
const yaz = (anahtar, deger) => {
  try {
    localStorage.setItem(anahtar, JSON.stringify(deger));
    return true;
  } catch {
    return false;
  }
};

// iPhone/iPad (iPadOS kendini Mac olarak tanıtır, dokunmatikle ayırt edilir)
const IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const saatYaz = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

/** İki seçenekli, seçili zemini kayan geçiş düğmesi. */
function IkiliSecim({ id, secenekler, deger, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800 w-full sm:w-auto">
      {secenekler.map((x) => (
        <button
          key={x.v}
          type="button"
          onClick={() => onChange(x.v)}
          aria-pressed={deger === x.v}
          className={`relative h-11 sm:h-9 px-2 sm:px-3 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors ${
            deger === x.v ? "text-neutral-950 dark:text-white" : "text-neutral-500 dark:text-neutral-400"
          }`}
        >
          {deger === x.v && (
            <motion.span
              layoutId={id}
              className="absolute inset-0 rounded-lg bg-white dark:bg-neutral-950 shadow-sm"
              transition={{ type: "spring", stiffness: 420, damping: 36 }}
            />
          )}
          <span className="relative flex flex-col sm:flex-row items-center justify-center leading-tight sm:gap-1">
            {x.l}
            <span className="tabular-nums text-[11px] text-neutral-400">{x.sayi}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export default function DavetPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secili, setSecili] = useState(() => new Set(STATUSES));
  const [kayitliMetin, setKayitliMetin] = useState(() => oku(METIN_ANAHTAR, VARSAYILAN_METIN));
  const [metin, setMetin] = useState(kayitliMetin);
  const [gonderilen, setGonderilen] = useState(() => oku(GONDERILEN_ANAHTAR, {}));
  const [gorunum, setGorunum] = useState("bekleyen"); // "bekleyen" | "gonderilen"
  const [secilenNumara, setSecilenNumara] = useState({}); // id → numara (birincil dışı seçim)
  const [acikNumara, setAcikNumara] = useState(null);
  const [sifirlaAcik, setSifirlaAcik] = useState(false);
  const [rehberHazirlaniyor, setRehberHazirlaniyor] = useState(false);
  const [bilgiAcik, setBilgiAcik] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setMembers(await api.listMembers());
      } catch {
        toast.error("Üyeler yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    yaz(GONDERILEN_ANAHTAR, gonderilen);
  }, [gonderilen]);

  const hedefler = useMemo(
    () =>
      members
        .filter((m) => secili.has(m.status))
        .sort((a, b) => (a.order_no ?? 1e9) - (b.order_no ?? 1e9)),
    [members, secili]
  );
  const cepli = useMemo(() => hedefler.filter((m) => birincilCep(m)), [hedefler]);
  const cepsiz = hedefler.length - cepli.length;
  const bekleyenler = cepli.filter((m) => !gonderilen[m.id]);
  const gonderilenler = cepli
    .filter((m) => gonderilen[m.id])
    .sort((a, b) => String(gonderilen[b.id]).localeCompare(String(gonderilen[a.id])));
  const liste = gorunum === "bekleyen" ? bekleyenler : gonderilenler;
  const siradaki = bekleyenler[0] || null;
  const degisti = metin !== kayitliMetin;

  const kisiselMetin = (m) => metin.replaceAll("{ad}", basHarfBuyuk(m.name));
  const numaraOf = (m) => secilenNumara[m.id] || birincilCep(m)?.numara;

  const gonderildiIsaretle = (m) =>
    setGonderilen((g) => ({ ...g, [m.id]: new Date().toISOString() }));

  const durumCevir = (s) =>
    setSecili((prev) => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);
      else n.add(s);
      return n;
    });

  const metniKaydet = () => {
    if (yaz(METIN_ANAHTAR, metin)) {
      setKayitliMetin(metin);
      toast.success("Mesaj kaydedildi");
    } else {
      toast.error("Bu cihazda kaydedilemedi");
    }
  };

  const rehbereKaydet = async () => {
    const vcf = vcardMetni(cepli, KISA_AD);
    const dosyaAdi = `${KISA_AD}-uyeler-${cepli.length}.vcf`;
    if (IOS) {
      // iPhone indirilen dosyayı Kişiler'e aktarmıyor; Safari "Tüm kişileri
      // ekle" ekranını yalnız gerçek bir adresten gelen dosya için açıyor.
      setRehberHazirlaniyor(true);
      try {
        const { kod } = await api.vcardBirak(vcf, dosyaAdi);
        window.location.href = api.vcardUrl(kod);
      } catch {
        toast.error("Rehber dosyası hazırlanamadı, tekrar deneyin");
      } finally {
        setRehberHazirlaniyor(false);
      }
      return;
    }
    const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = dosyaAdi;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`${cepli.length} kişilik rehber dosyası indirildi`);
  };

  const ornek = siradaki || cepli[0];
  const gonderilenSayi = gonderilenler.length;

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-neutral-950">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-5 sm:py-6 space-y-4 pb-28">
        <div className="flex items-start gap-3">
          <Link
            to="/sandik"
            aria-label="Sandık Takibi'ne dön"
            className="mt-1 h-9 w-9 shrink-0 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex items-center justify-center text-neutral-600 dark:text-neutral-300"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
              Seçime çağrı
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-0.5 text-neutral-950 dark:text-neutral-50">
              WhatsApp Davet
            </h1>
            <p className="text-[12.5px] text-neutral-500 dark:text-neutral-400 mt-1">
              Her üyeye kendi adıyla, ayrı sohbet olarak mesaj gönderin. Grup kurmaya ya da numaraları
              rehbere kaydetmeye gerek yok.
            </p>
          </div>
        </div>

        {/* Kısa bilgi; ayrıntı "Daha fazla" ile açılır (mobilde yer kaplamasın) */}
        <div className="flex gap-2.5 rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/30 px-3 py-2.5 text-[12px] leading-relaxed text-sky-900 dark:text-sky-200">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            "Gönder"e basın, WhatsApp mesaj yazılı hâlde açılır; gönderip buraya dönün.{" "}
            <button
              type="button"
              onClick={() => setBilgiAcik((v) => !v)}
              className="font-semibold underline underline-offset-2"
              aria-expanded={bilgiAcik}
            >
              {bilgiAcik ? "Daha az" : "Daha fazla"}
            </button>
            {bilgiAcik && (
              <p className="mt-1.5">
                WhatsApp'ın kendi <i>toplu gönderim listesi</i> yalnız sizin numaranızı rehberine kaydetmiş
                kişilere ulaşır ve 256 kişiyle sınırlıdır; bu yüzden tek tek gönderim daha garantili. Çok hızlı
                art arda gönderim WhatsApp'ın spam korumasına takılabilir; mesajın kişiye özel olması bu riski
                azaltır.
              </p>
            )}
          </div>
        </div>

        {/* 1. Kime */}
        <section className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-heading text-base font-semibold text-neutral-950 dark:text-neutral-50">1. Kime gönderilecek?</h2>
            <div className="flex gap-1.5 whitespace-nowrap">
              <button
                type="button"
                onClick={() => setSecili(new Set(STATUSES))}
                className="h-8 px-2.5 rounded-lg text-[12px] font-medium border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300"
              >
                Hepsi
              </button>
              <button
                type="button"
                onClick={() => setSecili(new Set(OUR_SIDE))}
                className="h-8 px-2.5 rounded-lg text-[12px] font-medium border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 inline-flex items-center gap-1"
              >
                <Users className="h-3.5 w-3.5" />
                Bizim taraf
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => {
              const meta = STATUS_META[s];
              const on = secili.has(s);
              const sayi = members.filter((m) => m.status === s).length;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => durumCevir(s)}
                  aria-pressed={on}
                  className={`h-9 px-3 rounded-full border text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                    on
                      ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950 dark:border-white"
                      : "bg-white dark:bg-neutral-900 text-neutral-500 border-neutral-200 dark:border-neutral-700"
                  }`}
                >
                  <span className="h-2 w-2 rounded-full ring-1 ring-black/10" style={{ background: meta.dot, opacity: on ? 1 : 0.5 }} />
                  {meta.short}
                  <span className="tabular-nums opacity-60">{sayi}</span>
                </button>
              );
            })}
          </div>
          <div className="text-[12px] text-neutral-500 dark:text-neutral-400 mt-3">
            {loading ? "Yükleniyor…" : (
              <>
                <b className="text-neutral-900 dark:text-neutral-100">{cepli.length}</b> kişiye gönderilebilir
                {cepsiz > 0 && ` · ${cepsiz} kişinin cep numarası yok`}
              </>
            )}
          </div>

          {/* Rehbere toplu kaydet: seçili gruba uygulanır, o yüzden burada */}
          <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="min-w-0 flex-1 text-[12px] text-neutral-500 dark:text-neutral-400">
              <div className="flex items-center gap-1.5 font-semibold text-[13px] text-neutral-900 dark:text-neutral-100">
                <ContactRound className="h-4 w-4" />
                Rehbere toplu kaydet
              </div>
              <p className="mt-0.5">
                Seçili {cepli.length} kişi telefon rehberine eklenir. Adların başında <b>{KISA_AD}</b> yazar;
                sonradan hepsi kolayca bulunup silinir.
                {IOS && " iPhone'da açılan ekranda \"Tüm Kişileri Ekle\"ye basın."}
              </p>
            </div>
            <button
              type="button"
              onClick={rehbereKaydet}
              disabled={cepli.length === 0 || rehberHazirlaniyor}
              className="shrink-0 h-11 sm:h-10 px-4 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              data-testid="davet-vcf"
            >
              <ContactRound className="h-4 w-4" />
              {rehberHazirlaniyor ? "Hazırlanıyor…" : "Rehbere kaydet"}
            </button>
          </div>
        </section>

        {/* 2. Mesaj */}
        <section className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="font-heading text-base font-semibold text-neutral-950 dark:text-neutral-50">2. Mesaj</h2>
            {metin !== VARSAYILAN_METIN && (
              <button
                type="button"
                onClick={() => setMetin(VARSAYILAN_METIN)}
                className="text-[12px] text-neutral-500 hover:text-neutral-900 dark:hover:text-white inline-flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Hazır metne dön
              </button>
            )}
          </div>
          <Textarea
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            rows={8}
            className="text-[14px] leading-relaxed bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100"
            data-testid="davet-metin"
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
            <p className="flex-1 text-[11px] text-neutral-500 dark:text-neutral-400">
              <code className="px-1 rounded bg-neutral-100 dark:bg-neutral-800">{"{ad}"}</code> yazan yere her
              üyenin kendi adı gelir.
              {degisti && (
                <span className="ml-1 font-semibold text-amber-700 dark:text-amber-400">
                  Kaydedilmemiş değişiklik var.
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={metniKaydet}
              disabled={!degisti}
              className="h-10 px-4 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 bg-neutral-950 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 disabled:opacity-40"
              data-testid="davet-kaydet"
            >
              {degisti ? <Save className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              {degisti ? "Kaydet" : "Kaydedildi"}
            </button>
          </div>
          {ornek && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-1">
                Önizleme — {basHarfBuyuk(ornek.name)}
              </div>
              <div className="rounded-xl rounded-tl-sm bg-[#DCF8C6] dark:bg-emerald-900/40 text-neutral-900 dark:text-emerald-50 px-3 py-2 text-[13.5px] whitespace-pre-wrap max-w-md shadow-sm">
                {kisiselMetin(ornek)}
              </div>
            </div>
          )}
        </section>

        {/* 3. Gönder */}
        <section className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-heading text-base font-semibold text-neutral-950 dark:text-neutral-50">3. Gönder</h2>
            <div className="text-[12px] tabular-nums text-neutral-600 dark:text-neutral-300">
              <b>{gonderilenSayi}</b> / {cepli.length} gönderildi
            </div>
          </div>
          <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden mt-2">
            <div
              className="h-full bg-emerald-500 transition-[width] duration-500"
              style={{ width: `${cepli.length ? (gonderilenSayi / cepli.length) * 100 : 0}%` }}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4 mb-2">
            <IkiliSecim
              id="davet-gorunum-zemin"
              deger={gorunum}
              onChange={setGorunum}
              secenekler={[
                { v: "bekleyen", l: "Gönderilmeyenler", sayi: bekleyenler.length },
                { v: "gonderilen", l: "Gönderilenler", sayi: gonderilenSayi },
              ]}
            />
            {gonderilenSayi > 0 && (
              <button
                type="button"
                onClick={() => setSifirlaAcik(true)}
                className="self-end sm:self-auto text-[12px] text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                İşaretleri sıfırla
              </button>
            )}
          </div>

          {liste.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-500">
              {cepli.length === 0
                ? "Seçili grupta cep numarası olan üye yok."
                : gorunum === "bekleyen"
                ? "Hepsine gönderildi. 🎉"
                : "Henüz kimseye gönderilmedi."}
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {liste.map((m) => {
                const zaman = gonderilen[m.id];
                const numara = numaraOf(m);
                const cepler = telefonlar(m.phone).filter((t) => t.cep);
                const meta = STATUS_META[m.status] || STATUS_META.Beyaz;
                return (
                  <li key={m.id} className="py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-black/10" style={{ background: meta.dot }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                          {m.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => setAcikNumara((a) => (a === m.id ? null : m.id))}
                          className="text-[11.5px] text-neutral-500 dark:text-neutral-400 font-mono inline-flex flex-wrap items-center gap-x-1 text-left"
                        >
                          <span className="whitespace-nowrap">{numaraYaz(numara)}</span>
                          {cepler.length > 1 && (
                            <span className="font-sans text-[10.5px] text-sky-700 dark:text-sky-400 inline-flex items-center whitespace-nowrap">
                              · {cepler.length} numara <ChevronDown className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                        {zaman && (
                          <div className="text-[11px] text-emerald-700 dark:text-emerald-400">
                            ✓ Gönderildi · {saatYaz(zaman)}
                          </div>
                        )}
                      </div>
                      <a
                        href={waLinki(numara, kisiselMetin(m))}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => gonderildiIsaretle(m)}
                        data-testid={`davet-gonder-${m.id}`}
                        className={`shrink-0 h-10 px-3.5 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 ${
                          zaman
                            ? "border border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40"
                            : "bg-[#25D366] text-white hover:bg-[#1eb957]"
                        }`}
                      >
                        <Send className="h-4 w-4" />
                        {zaman ? "Tekrar gönder" : "Gönder"}
                      </a>
                    </div>
                    {acikNumara === m.id && cepler.length > 1 && (
                      <div className="mt-2 ml-5 flex flex-wrap gap-1.5">
                        {cepler.map((t) => (
                          <button
                            key={t.numara}
                            type="button"
                            onClick={() => {
                              setSecilenNumara((s) => ({ ...s, [m.id]: t.numara }));
                              setAcikNumara(null);
                            }}
                            className={`h-8 px-2.5 rounded-lg border text-[11.5px] ${
                              t.numara === numara
                                ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300"
                                : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300"
                            }`}
                          >
                            <span className="font-mono">{numaraYaz(t.numara)}</span>
                            {t.etiket && <span className="ml-1 opacity-70">{t.etiket}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>

      {/* Sıradakine gönder: tek elle hızlı ilerlemek için altta sabit */}
      {siradaki && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-md border-t border-neutral-200 dark:border-neutral-800"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="max-w-3xl mx-auto px-4 py-3">
            <a
              href={waLinki(numaraOf(siradaki), kisiselMetin(siradaki))}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gonderildiIsaretle(siradaki)}
              data-testid="davet-siradaki"
              className="h-12 w-full rounded-xl bg-[#25D366] hover:bg-[#1eb957] text-white font-semibold text-[15px] flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4" />
              <span className="truncate">Sıradakine gönder: {basHarfBuyuk(siradaki.name)}</span>
              <span className="text-white/80 text-[12px] tabular-nums shrink-0">
                {gonderilenSayi + 1}/{cepli.length}
              </span>
            </a>
          </div>
        </div>
      )}

      <AlertDialog open={sifirlaAcik} onOpenChange={setSifirlaAcik}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gönderildi işaretleri sıfırlansın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu cihazdaki {gonderilenSayi} "gönderildi" işareti silinir; yeni bir mesaj turuna baştan
              başlarsınız. Gönderilmiş WhatsApp mesajları etkilenmez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setGonderilen({});
                toast.success("İşaretler sıfırlandı");
              }}
            >
              Sıfırla
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
